<script>
import axios from 'axios'
import FirewallDetail from './firewall_detail'

const REFRESH_INTERVAL_MS = 60000

export default {
  components: {
    FirewallDetail
  },
  props: {
    basePath: {
      type: String,
      default: '/monitoring/firewalls'
    }
  },
  data () {
    return {
      // eslint-disable-next-line
      appConfig: process.env.NODE_ENV === 'production' ? require('@src/app.config') : require('@src/app.config.dev'),
      summary: {},
      alerts: [],
      alertSeverity: 'all',
      charts: {},
      fleet: [],
      totalRows: 0,
      haOnly: false,
      currentPage: 1,
      perPage: 25,
      pageOptions: [10, 25, 50, 100],
      sortBy: 'ip',
      sortDesc: false,
      filter: '',
      isBusy: false,
      // False until the first load answers: the empty-state messages must not
      // show while the request is still in flight.
      loaded: false,
      loadError: '',
      lastUpdated: null,
      refreshTimer: null,
      selectedDevice: '',
      severityOptions: [
        { value: 'all', text: 'All severities' },
        { value: 'critical', text: 'Critical' },
        { value: 'warning', text: 'Warning' },
        { value: 'info', text: 'Info' }
      ],
      fields: [
        { key: 'device', label: 'Device', sortable: true, tdClass: 'ha-cell', thClass: 'ha-cell-head' },
        { key: 'ip', label: 'IP', sortable: true },
        { key: 'model', label: 'Model', sortable: true },
        { key: 'sw_version', label: 'Version', sortable: true },
        { key: 'uptime', label: 'Uptime', sortable: true },
        { key: 'local_state', label: 'HA State', sortable: true },
        { key: 'peer_state', label: 'Peer', sortable: false },
        { key: 'running_sync', label: 'Sync', sortable: true },
        { key: 'last_seen', label: 'Last Seen', sortable: true },
        { key: 'status', label: 'Status', sortable: true }
      ]
    }
  },
  computed: {
    // ha_enabled is the firewall's own flag rather than a guess from the name
    // or the state, and it separates the fleet cleanly: the standalone units
    // report it false, every member of a pair reports it true.
    visibleFleet () {
      if (!this.haOnly) return this.fleet
      return this.fleet.filter((row) => row.ha_enabled)
    },
    // Which HA pair each device belongs to, keyed by its own address. The pair
    // comes from the peer the firewall itself reports, not from guessing that
    // neighbouring addresses belong together, so it stays right even when a
    // member is missing or the two were labelled inconsistently.
    haPairs () {
      const byIp = {}
      this.fleet.forEach((row) => { if (row.ip) byIp[row.ip] = row })

      const seen = {}
      const pairs = []
      this.fleet.forEach((row) => {
        if (!row.ip || !row.peer_ip || seen[row.ip]) return
        const peer = byIp[row.peer_ip]
        if (!peer) return
        seen[row.ip] = true
        seen[peer.ip] = true
        pairs.push([row.ip, peer.ip])
      })

      // Ordering the pairs by their lowest address keeps each one on the same
      // tone from one refresh to the next; alternating the tone is what makes
      // the boundary visible when two pairs end up on consecutive rows.
      pairs.sort((a, b) => this.ipSortValue(a[0]) - this.ipSortValue(b[0]))
      const marks = {}
      pairs.forEach((pair, index) => {
        pair.forEach((ip) => { marks[ip] = index % 2 === 0 ? 'a' : 'b' })
      })
      return marks
    },
    baseURL () {
      return this.appConfig.xdrAPI.baseURL || 'http://localhost:5000'
    },
    collector () {
      return this.summary.collector || {}
    },
    collectorAgeText () {
      const seconds = this.collector.age_seconds
      if (typeof seconds !== 'number') return 'unknown'
      if (seconds < 3600) return `${Math.round(seconds / 60)} min ago`
      return `${(seconds / 3600).toFixed(1)} h ago`
    },
    // Devices whose inventory label and self-reported hostname disagree.
    // Surfaced quietly: it is a data-quality finding, not an operational alarm.
    nameIssues () {
      return (this.fleet || [])
        .filter((item) => ['mismatch', 'placeholder', 'factory-default']
          .includes(item.name_status))
        .sort((a, b) => a.device.localeCompare(b.device))
    },
    displayNames () {
      const mapa = {}
      for (const item of this.fleet) {
        mapa[item.device] = item.display_name || item.device
      }
      return mapa
    },
    filteredAlerts () {
      if (this.alertSeverity === 'all') return this.alerts
      return this.alerts.filter((alert) => alert.severity === this.alertSeverity)
    },
    uptimeBucketSeries () {
      const distribution = this.charts.uptime_distribution || []
      return [{ name: 'Devices', data: distribution.map((bucket) => bucket.count) }]
    },
    uptimeBucketOptions () {
      const distribution = this.charts.uptime_distribution || []
      return {
        chart: { type: 'bar', toolbar: { show: false } },
        plotOptions: { bar: { columnWidth: '45%', borderRadius: 3 } },
        dataLabels: { enabled: false },
        colors: ['#727cf5'],
        xaxis: { categories: distribution.map((bucket) => bucket.bucket) },
        yaxis: { labels: { formatter: (value) => Math.round(value) } }
      }
    },
    versionSeries () {
      const versions = this.charts.versions || []
      return [{ name: 'Devices', data: versions.map((row) => row.count) }]
    },
    versionOptions () {
      const versions = this.charts.versions || []
      return {
        chart: { type: 'bar', toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, barHeight: '55%', borderRadius: 3 } },
        dataLabels: { enabled: true },
        colors: ['#0acf97'],
        xaxis: { categories: versions.map((row) => row.value) }
      }
    },
    modelSeries () {
      return (this.charts.models || []).map((row) => row.count)
    },
    modelOptions () {
      const models = this.charts.models || []
      return {
        chart: { type: 'donut' },
        labels: models.map((row) => row.value),
        legend: { position: 'bottom' },
        dataLabels: { enabled: false }
      }
    }
  },
  watch: {
    // b-table only reports a new row count through @filtered, which fires for
    // the search box and not for a change of items, so the count behind the
    // pager and the badge is refreshed here.
    haOnly () {
      this.totalRows = this.visibleFleet.length
      this.currentPage = 1
    }
  },
  mounted () {
    this.loadAll()
    // Do not poll from background tabs (see sdwan.vue for rationale).
    this.refreshTimer = setInterval(() => {
      if (document.hidden) return
      this.loadAll()
    }, REFRESH_INTERVAL_MS)
  },
  beforeDestroy () {
    if (this.refreshTimer) clearInterval(this.refreshTimer)
  },
  methods: {
    // An address has to be compared as four numbers, not as text: sorted as
    // text 10.30.48.10 lands before 10.30.48.9, and 10.116.x before 10.18.x,
    // which is exactly what breaks the pairing this ordering is for.
    ipSortValue (value) {
      const parts = String(value || '').split('.')
      if (parts.length !== 4) return Number.MAX_SAFE_INTEGER
      let out = 0
      for (const part of parts) {
        const octet = Number(part)
        if (!Number.isInteger(octet) || octet < 0 || octet > 255) {
          // Anything that is not an address sorts last rather than landing
          // between two real ones and splitting a pair.
          return Number.MAX_SAFE_INTEGER
        }
        out = out * 256 + octet
      }
      return out
    },
    // The two members of an HA pair sit on consecutive addresses, so ordering
    // by IP puts each pair on adjacent rows and its state can be read at a
    // glance. Returning null for every other column leaves b-table's own
    // comparison in place.
    // Configuration sync is reported for every member of this fleet as "not
    // synchronized" and has never read otherwise, so it is shown plainly
    // rather than in a warning colour. The tooltip names the sync that does
    // decide whether a failover keeps its connections.
    syncNote (row) {
      const note = 'Configuration sync between the two members.'
      if (!row || !row.state_sync) return note
      return `${note} Session sync over HA2: ${row.state_sync}, which is what decides whether a failover keeps established connections.`
    },
    haMark (row) {
      if (!row || !row.ip) return null
      if (this.haPairs[row.ip]) return this.haPairs[row.ip]
      // An HA member with no partner on the table. It is not an error in the
      // filter: the peer is simply not being collected, and marking it as a
      // broken line says so instead of leaving the row looking unexplained.
      return row.ha_enabled ? 'alone' : null
    },
    haMarkTitle (row) {
      const mark = this.haMark(row)
      if (!mark) return ''
      if (mark !== 'alone') return `HA pair with ${row.peer_ip}`
      return row.peer_ip
        ? `HA member, but its peer ${row.peer_ip} is not reporting - nothing is being collected from it`
        : 'HA member with no peer reported'
    },
    // Where this row sits inside its pair, so the line can stop short at the
    // pair's outer ends and run straight through the join. Only meaningful
    // while the table is in its default order: any other column can put the
    // two members apart, and then a half-bracket would point at nothing, so
    // the mark falls back to spanning the whole cell.
    haMarkPosition (row) {
      if (this.sortBy !== 'ip' || this.sortDesc) return ''
      const peerIp = row && row.peer_ip
      if (!peerIp || !this.haPairs[row.ip]) return ''
      return this.ipSortValue(row.ip) < this.ipSortValue(peerIp) ? 'top' : 'bottom'
    },
    sortCompare (a, b, key) {
      if (key !== 'ip') return null
      return this.ipSortValue(a.ip) - this.ipSortValue(b.ip)
    },
    parse (data) {
      return typeof data === 'string' ? JSON.parse(data) : data
    },
    loadAll () {
      // Never stack a refresh cycle on top of one still in flight.
      if (this.isBusy) return
      this.isBusy = true
      this.loadError = ''
      const base = `${this.baseURL}${this.basePath}`
      Promise.all([
        axios.get(`${base}/summary`),
        axios.get(`${base}/fleet`),
        axios.get(`${base}/alerts`),
        axios.get(`${base}/charts`)
      ])
        .then(([summary, fleet, alerts, charts]) => {
          this.summary = this.parse(summary.data)
          const fleetData = this.parse(fleet.data)
          this.fleet = fleetData.items || []
          // Counted off the visible rows, not the whole fleet: the auto refresh
          // runs every minute and would otherwise put the total back to the
          // full count while the HA filter is on.
          this.totalRows = this.visibleFleet.length
          this.alerts = this.parse(alerts.data).items || []
          this.charts = this.parse(charts.data)
          this.lastUpdated = new Date()
          // Only a successful load counts: after a failure the panels say so instead
          // of claiming there is nothing to show.
          this.loaded = true
        })
        .catch((error) => {
          this.loadError = `Could not load firewall monitoring data: ${error.message}`
        })
        .finally(() => {
          this.isBusy = false
        })
    },
    onFiltered (filteredItems) {
      this.totalRows = filteredItems.length
      this.currentPage = 1
    },
    showDetails (item) {
      this.selectedDevice = item.device
      this.$refs['modal-details'].show()
    },
    onDetailsShown () {
      // ApexCharts sizes itself against the width available when it renders,
      // and inside a modal that width is not final until the dialog is shown.
      // Safari is the strictest about it: without this the chart keeps a stale
      // width and paints outside the modal.
      this.$nextTick(() => window.dispatchEvent(new Event('resize')))
    },
    openNames () {
      this.$refs['modal-names'].show()
    },
    nameNote (item) {
      const notas = {
        mismatch: `Inventory label: ${item.device} · device reports: ${item.hostname}`,
        placeholder: `The inventory has no name for this device (${item.device}); it identifies itself as ${item.hostname}`,
        'factory-default': `The device has no hostname configured: it answers with its model (${item.hostname})`
      }
      return notas[item.name_status] || ''
    },
    formatDate (value) {
      if (!value) return '—'
      return new Date(value).toLocaleString('en-CO', {
        timeZone: 'America/Bogota',
        hour12: false
      })
    },
    humanUptime (seconds, item) {
      if (item && item.uptime_text) return item.uptime_text
      if (typeof seconds !== 'number') return '—'
      const days = Math.floor(seconds / 86400)
      const hours = Math.floor((seconds % 86400) / 3600)
      return `${days}d ${hours}h`
    },
    statusVariant (status) {
      const variants = {
        ok: 'success',
        'degraded-pair': 'warning',
        impaired: 'danger',
        unreachable: 'danger',
        stale: 'secondary'
      }
      return variants[status] || 'secondary'
    },
    stateVariant (state) {
      if (state === 'active' || state === 'standalone') return 'success'
      if (state === 'passive') return 'info'
      if (!state) return 'secondary'
      return 'danger'
    },
    severityVariant (severity) {
      const variants = { critical: 'danger', warning: 'warning', info: 'secondary' }
      return variants[severity] || 'secondary'
    }
  }
}
</script>

<template>
  <div class="firewalls-page">
    <b-alert
      :show="loadError.length > 0"
      variant="danger"
      dismissible
      @dismissed="loadError = ''"
    >
      {{ loadError }}
    </b-alert>

    <b-alert
      :show="collector.status === 'down'"
      variant="warning"
      class="d-flex align-items-center py-1"
    >
      <feather
        type="alert-triangle"
        class="icon-xs mr-2"
      />
      <span>
        <b>Collector down</b> — no new samples for {{ collectorAgeText.replace(' ago', '') }}
        (expected every 15 min). The data below is the last known state; live KPIs such as
        <i>Reporting</i> and <i>Unreachable</i> read 0 until the collector runs again.
      </span>
    </b-alert>

    <!-- Toolbar -->
    <div class="d-flex align-items-center mb-2">
      <span
        v-if="lastUpdated"
        class="text-muted font-size-12"
      >
        Updated {{ formatDate(lastUpdated) }} (America/Bogota) · auto-refresh 60 s
      </span>
      <b-button
        size="sm"
        variant="outline-primary"
        class="ml-auto"
        :disabled="isBusy"
        @click="loadAll"
      >
        <feather
          type="refresh-cw"
          class="icon-xs mr-1"
        />
        Refresh
      </b-button>
    </div>

    <!-- KPI cards -->
    <div class="row">
      <div class="col-xl-2 col-md-4 col-6 mb-4">
        <div class="card h-100 mb-0">
          <div class="card-body py-2">
            <div class="d-flex align-items-center">
              <div class="flex-grow-1">
                <h5 class="text-muted font-size-13 mb-1">
                  Reporting
                  <feather
                    v-b-tooltip.hover
                    type="help-circle"
                    class="icon-xs help-tip"
                    title="Devices whose latest sample is less than 45 minutes old, out of all devices seen in the last 7 days."
                  />
                </h5>
                <h3 class="mb-0">
                  {{ summary.devices_reporting || 0 }}<small class="text-muted font-size-13">/{{ summary.devices_known || 0 }}</small>
                </h3>
              </div>
              <div class="avatar-sm rounded-circle align-self-center bg-primary">
                <span class="avatar-title bg-transparent">
                  <feather
                    type="activity"
                    class="icon-sm"
                  />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-xl-2 col-md-4 col-6 mb-4">
        <div class="card h-100 mb-0">
          <div class="card-body py-2">
            <div class="d-flex align-items-center">
              <div class="flex-grow-1">
                <h5 class="text-muted font-size-13 mb-1">
                  Unreachable
                  <feather
                    v-b-tooltip.hover
                    type="help-circle"
                    class="icon-xs help-tip"
                    title="Devices with a connect or timeout collector error in the last 20 minutes: the site, circuit or device is likely down."
                  />
                </h5>
                <h3
                  class="mb-0"
                  :class="{ 'text-danger': (summary.devices_unreachable || 0) > 0 }"
                >
                  {{ summary.devices_unreachable || 0 }}
                </h3>
              </div>
              <div class="avatar-sm rounded-circle align-self-center bg-secondary">
                <span class="avatar-title bg-transparent">
                  <feather
                    type="wifi-off"
                    class="icon-sm"
                  />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-xl-2 col-md-4 col-6 mb-4">
        <div class="card h-100 mb-0">
          <div class="card-body py-2">
            <div class="d-flex align-items-center">
              <div class="flex-grow-1">
                <h5 class="text-muted font-size-13 mb-1">
                  HA Degraded
                  <feather
                    v-b-tooltip.hover
                    type="help-circle"
                    class="icon-xs help-tip"
                    title="HA pairs that are still redundant but one failure away from an outage: the peer link is down or the pair is not synchronized."
                  />
                </h5>
                <h3
                  class="mb-0"
                  :class="{ 'text-warning': (summary.ha_degraded || 0) > 0 }"
                >
                  {{ summary.ha_degraded || 0 }}<small class="text-muted font-size-13">/{{ summary.ha_members || 0 }}</small>
                </h3>
              </div>
              <div class="avatar-sm rounded-circle align-self-center bg-warning">
                <span class="avatar-title bg-transparent">
                  <feather
                    type="alert-triangle"
                    class="icon-sm"
                  />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-xl-2 col-md-4 col-6 mb-4">
        <div class="card h-100 mb-0">
          <div class="card-body py-2">
            <div class="d-flex align-items-center">
              <div class="flex-grow-1">
                <h5 class="text-muted font-size-13 mb-1">
                  HA Impaired
                  <feather
                    v-b-tooltip.hover
                    type="help-circle"
                    class="icon-xs help-tip"
                    title="HA members in suspended, non-functional or initial state: they cannot take over if the active member fails."
                  />
                </h5>
                <h3
                  class="mb-0"
                  :class="{ 'text-danger': (summary.ha_impaired || 0) > 0 }"
                >
                  {{ summary.ha_impaired || 0 }}
                </h3>
              </div>
              <div class="avatar-sm rounded-circle align-self-center bg-danger">
                <span class="avatar-title bg-transparent">
                  <feather
                    type="alert-octagon"
                    class="icon-sm"
                  />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-xl-2 col-md-4 col-6 mb-4">
        <div class="card h-100 mb-0">
          <div class="card-body py-2">
            <div class="d-flex align-items-center">
              <div class="flex-grow-1">
                <h5 class="text-muted font-size-13 mb-1">
                  Reboots (24 h)
                  <feather
                    v-b-tooltip.hover
                    type="help-circle"
                    class="icon-xs help-tip"
                    title="Devices whose uptime counter reset in the last 24 hours, meaning the firewall restarted."
                  />
                </h5>
                <h3
                  class="mb-0"
                  :class="{ 'text-warning': (summary.reboots_24h || 0) > 0 }"
                >
                  {{ summary.reboots_24h || 0 }}
                </h3>
              </div>
              <div class="avatar-sm rounded-circle align-self-center bg-warning">
                <span class="avatar-title bg-transparent">
                  <feather
                    type="rotate-ccw"
                    class="icon-sm"
                  />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-xl-2 col-md-4 col-6 mb-4">
        <div class="card h-100 mb-0">
          <div class="card-body py-2">
            <div class="d-flex align-items-center">
              <div class="flex-grow-1">
                <h5 class="text-muted font-size-13 mb-1">
                  Collector
                  <feather
                    v-b-tooltip.hover
                    type="help-circle"
                    class="icon-xs help-tip"
                    title="Heartbeat of the uptime/HA collector. It runs every 15 minutes; no run for more than 35 minutes raises the alarm and the data below becomes the last known state."
                  />
                </h5>
                <h3 class="mb-0">
                  <b-badge :variant="collector.status === 'ok' ? 'success' : 'danger'">
                    {{ collector.status || 'unknown' }}
                  </b-badge>
                </h3>
                <span class="text-muted font-size-11">last run {{ collectorAgeText }}</span>
              </div>
              <div
                class="avatar-sm rounded-circle align-self-center"
                :class="collector.status === 'ok' ? 'bg-success' : 'bg-danger'"
              >
                <span class="avatar-title bg-transparent">
                  <feather
                    type="radio"
                    class="icon-sm"
                  />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Charts -->
    <div class="row">
      <div class="col-lg-4 mb-4">
        <div class="card h-100 mb-0">
          <div class="card-body py-2">
            <h5 class="font-size-14 mb-1">
              <feather
                type="bar-chart-2"
                class="icon-xs mr-1 text-muted"
              />
              Uptime distribution
              <feather
                v-b-tooltip.hover
                type="help-circle"
                class="icon-xs help-tip"
                title="How long each firewall has been running since its last reboot, grouped into age bands. A large '<1d' band right after a maintenance window is expected; otherwise it points at unplanned restarts."
              />
            </h5>
            <apexchart
              v-if="(charts.uptime_distribution || []).length"
              type="bar"
              height="220"
              :series="uptimeBucketSeries"
              :options="uptimeBucketOptions"
            />
            <div
              v-else-if="loadError"
              class="text-danger py-3 text-center font-size-13"
            >
              Could not load this section.
            </div>
            <div
              v-else-if="!loaded"
              class="text-muted py-3 text-center"
            >
              <b-spinner small />&nbsp;&nbsp;Loading...
            </div>
            <div
              v-else
              class="text-muted py-4 text-center"
            >
              No data yet.
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-4 mb-4">
        <div class="card h-100 mb-0">
          <div class="card-body py-2">
            <h5 class="font-size-14 mb-1">
              <feather
                type="tag"
                class="icon-xs mr-1 text-muted"
              />
              PAN-OS versions
              <feather
                v-b-tooltip.hover
                type="help-circle"
                class="icon-xs help-tip"
                title="Firmware inventory across the fleet, from each device's latest sample. Useful as a compliance and patch-planning view."
              />
            </h5>
            <apexchart
              v-if="(charts.versions || []).length"
              type="bar"
              height="220"
              :series="versionSeries"
              :options="versionOptions"
            />
            <div
              v-else-if="loadError"
              class="text-danger py-3 text-center font-size-13"
            >
              Could not load this section.
            </div>
            <div
              v-else-if="!loaded"
              class="text-muted py-3 text-center"
            >
              <b-spinner small />&nbsp;&nbsp;Loading...
            </div>
            <div
              v-else
              class="text-muted py-4 text-center"
            >
              No data yet.
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-4 mb-4">
        <div class="card h-100 mb-0">
          <div class="card-body py-2">
            <h5 class="font-size-14 mb-1">
              <feather
                type="cpu"
                class="icon-xs mr-1 text-muted"
              />
              Models
              <feather
                v-b-tooltip.hover
                type="help-circle"
                class="icon-xs help-tip"
                title="Hardware model mix across the fleet, from each device's latest sample."
              />
            </h5>
            <apexchart
              v-if="(charts.models || []).length"
              type="donut"
              height="180"
              :series="modelSeries"
              :options="modelOptions"
            />
            <div
              v-else-if="loadError"
              class="text-danger py-3 text-center font-size-13"
            >
              Could not load this section.
            </div>
            <div
              v-else-if="!loaded"
              class="text-muted py-3 text-center"
            >
              <b-spinner small />&nbsp;&nbsp;Loading...
            </div>
            <div
              v-else
              class="text-muted py-4 text-center"
            >
              No data yet.
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Alerts -->
    <div class="row">
      <div class="col-12 mb-4">
        <div class="card mb-0">
          <div class="card-body py-2">
            <div class="d-flex align-items-center mb-2">
              <h5 class="font-size-14 mb-0">
                <feather
                  type="alert-circle"
                  class="icon-xs mr-1 text-danger"
                />
                Active alerts
                <b-badge
                  variant="light"
                  class="ml-1"
                >
                  {{ filteredAlerts.length }}
                </b-badge>
                <feather
                  v-b-tooltip.hover
                  type="help-circle"
                  class="icon-xs help-tip"
                  title="Critical = service is down or has lost its redundancy now (collector down, HA member unable to take over, split-brain suspicion, device unreachable). Warning = one step from that, or a notable change happened (degraded pair, failover, flap, reboot). Info = housekeeping, such as an expired API key."
                />
              </h5>
              <b-form-select
                v-model="alertSeverity"
                size="sm"
                :options="severityOptions"
                class="ml-auto"
                style="max-width: 160px;"
              />
            </div>
            <div
              v-if="filteredAlerts.length"
              style="max-height: min(260px, 32vh); overflow: auto;"
            >
              <div
                v-for="(alert, index) in filteredAlerts"
                :key="`alert-${index}`"
                class="row align-items-center border-bottom py-1 mx-0 font-size-13 flex-nowrap sdwan-fixed-row"
              >
                <div
                  class="px-0 text-center"
                  style="width: 70px; flex: none;"
                >
                  <b-badge :variant="severityVariant(alert.severity)">
                    {{ alert.severity }}
                  </b-badge>
                </div>
                <div
                  class="px-2 text-truncate font-weight-bold"
                  style="width: 220px; flex: none;"
                  :title="alert.device || 'collector'"
                >
                  {{ alert.device || 'collector' }}
                </div>
                <div
                  class="px-2 text-truncate"
                  style="width: 34%; flex: none;"
                  :title="alert.message"
                >
                  {{ alert.message }}
                </div>
                <div
                  class="col px-2 text-muted text-truncate"
                  :title="alert.detail"
                >
                  {{ alert.detail }}
                </div>
                <div
                  class="px-0 text-muted text-nowrap text-right font-size-11"
                  style="width: 135px; flex: none;"
                >
                  {{ formatDate(alert.ts) }}
                </div>
              </div>
            </div>
            <div
              v-else-if="loadError"
              class="text-danger py-3 text-center font-size-13"
            >
              Could not load this section.
            </div>
            <div
              v-else-if="!loaded"
              class="text-muted py-3 text-center"
            >
              <b-spinner small />&nbsp;&nbsp;Loading alerts...
            </div>
            <div
              v-else
              class="text-muted py-3 text-center"
            >
              No active alerts.
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Fleet table -->
    <div class="row">
      <div class="col-12 mb-4">
        <div class="card mb-0">
          <div class="card-body py-2">
            <div class="d-flex align-items-center mb-2">
              <!-- align-items-center lines up boxes, not text, and the switch
                   sits in a taller box than the heading, so its label came out
                   about 5px above the title. Baseline alignment on the pair of
                   them puts the two texts on the same line; the search box is
                   left out of it because a form control should stay centred. -->
              <div class="d-flex align-items-baseline">
                <h5 class="font-size-14 mb-0">
                  <feather
                    type="server"
                    class="icon-xs mr-1 text-muted"
                  />
                  Firewall fleet
                  <b-badge
                    variant="light"
                    class="ml-1"
                  >
                    {{ totalRows }}
                  </b-badge>
                  <feather
                    v-b-tooltip.hover
                    type="help-circle"
                    class="icon-xs help-tip"
                    title="Latest state per device. HA State is this member's own role (active, passive or standalone); Peer is its view of the other member. Status: ok, degraded-pair, impaired, unreachable, or stale when the newest sample is over 45 minutes old. Click a row for device details."
                  />
                  <a
                    v-if="nameIssues.length"
                    href="javascript:void(0)"
                    class="text-muted font-size-11 font-weight-normal ml-2"
                    @click="openNames"
                  >
                    {{ nameIssues.length }} naming inconsistencies
                  </a>
                </h5>
                <b-form-checkbox
                  v-model="haOnly"
                  switch
                  class="ml-3 font-size-13"
                >
                  HA only
                </b-form-checkbox>
              </div>
              <b-form-input
                v-model="filter"
                size="sm"
                placeholder="Search device, IP, model, state..."
                class="ml-auto"
                style="max-width: 280px;"
              />
            </div>

            <div class="table-responsive mb-0">
              <b-table
                :items="visibleFleet"
                :fields="fields"
                :busy="isBusy"
                :per-page="perPage"
                :current-page="currentPage"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                :sort-compare="sortCompare"
                :filter="filter"
                responsive="sm"
                show-empty
                hover
                empty-text="No firewalls reported inside the current window."
                @filtered="onFiltered"
                @row-clicked="showDetails"
              >
                <template v-slot:table-busy>
                  <div class="text-center">
                    <br><br>
                    <b-spinner class="align-middle" />
                    <strong>&nbsp;&nbsp;Loading...</strong>
                  </div>
                </template>

                <template v-slot:cell(device)="data">
                  <span
                    class="ha-mark"
                    :class="[
                      haMark(data.item) ? 'ha-mark--' + haMark(data.item) : 'ha-mark--none',
                      haMarkPosition(data.item) ? 'ha-mark--' + haMarkPosition(data.item) : '',
                    ]"
                    :title="haMarkTitle(data.item)"
                  />
                  <a
                    href="javascript:void(0)"
                    :title="data.item.display_name !== data.item.device ? `Inventory label: ${data.item.device}` : data.item.device"
                    @click.stop="showDetails(data.item)"
                  >{{ data.item.display_name || data.item.device }}</a>
                  <feather
                    v-if="nameNote(data.item)"
                    v-b-tooltip.hover
                    type="alert-circle"
                    class="icon-xs help-tip ml-1"
                    :title="nameNote(data.item)"
                  />
                </template>

                <template v-slot:cell(uptime)="data">
                  {{ humanUptime(data.item.uptime, data.item) }}
                </template>

                <template v-slot:cell(local_state)="data">
                  <b-badge :variant="stateVariant(data.item.local_state)">
                    {{ data.item.local_state || 'unknown' }}
                  </b-badge>
                </template>

                <template v-slot:cell(peer_state)="data">
                  <b-badge
                    v-if="data.item.peer_state"
                    :variant="stateVariant(data.item.peer_state)"
                  >
                    {{ data.item.peer_state }}
                  </b-badge>
                  <span
                    v-else
                    class="text-muted"
                  >—</span>
                </template>

                <template v-slot:cell(running_sync)="data">
                  <span
                    v-if="data.item.running_sync"
                    v-b-tooltip.hover
                    :class="data.item.running_sync === 'synchronized' ? 'text-success' : 'text-muted'"
                    :title="syncNote(data.item)"
                  >
                    {{ data.item.running_sync }}
                  </span>
                  <span
                    v-else
                    class="text-muted"
                  >—</span>
                </template>

                <template v-slot:cell(last_seen)="data">
                  {{ formatDate(data.item.last_seen) }}
                </template>

                <template v-slot:cell(status)="data">
                  <b-badge :variant="statusVariant(data.item.status)">
                    {{ data.item.status }}
                  </b-badge>
                </template>
              </b-table>
            </div>

            <div class="row">
              <div class="col-sm-12 col-md-6">
                <div class="dataTables_length">
                  <label class="d-inline-flex align-items-center">
                    Show&nbsp;
                    <b-form-select
                      v-model="perPage"
                      size="sm"
                      :options="pageOptions"
                    />&nbsp;entries
                  </label>
                </div>
              </div>
              <div class="col">
                <div class="dataTables_paginate paging_simple_numbers float-right">
                  <ul class="pagination pagination-rounded mb-0">
                    <b-pagination
                      v-model="currentPage"
                      :total-rows="totalRows"
                      :per-page="perPage"
                    />
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <b-modal
      id="firewall-names"
      ref="modal-names"
      size="lg"
      title="Naming inconsistencies"
      :centered="true"
      :no-fade="true"
      ok-only
      ok-title="Close"
    >
      <p class="text-muted font-size-12 mb-2">
        Each device is identified twice: by the label the collector's inventory
        carries, and by the hostname the device reports about itself. The list
        below is where the two disagree. It affects naming only — monitoring
        works from the inventory label, which is the key that joins the data.
      </p>
      <div
        class="table-responsive"
        style="max-height: min(420px, 45vh); overflow-y: auto;"
      >
        <table class="table table-sm font-size-12 mb-0">
          <thead>
            <tr class="text-muted">
              <th>Inventory label</th>
              <th>Device reports</th>
              <th>IP</th>
              <th>Finding</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in nameIssues"
              :key="`name-${item.device}`"
            >
              <td class="text-nowrap">
                {{ item.device }}
              </td>
              <td class="text-nowrap font-weight-bold">
                {{ item.hostname || '—' }}
              </td>
              <td class="text-muted text-nowrap">
                {{ item.ip || '—' }}
              </td>
              <td>
                <b-badge :variant="item.name_status === 'factory-default' ? 'warning' : 'light'">
                  {{ item.name_status === 'mismatch' ? 'names differ'
                    : item.name_status === 'placeholder' ? 'inventory has no name'
                      : 'device not named' }}
                </b-badge>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-modal>

    <b-modal
      id="firewall-details"
      ref="modal-details"
      size="xl"
      :title="`Firewall: ${selectedDevice}`"
      :centered="true"
      :no-fade="true"
      ok-only
      @shown="onDetailsShown"
    >
      <FirewallDetail
        :device="selectedDevice"
        :base-url="baseURL"
        :base-path="basePath"
      />
    </b-modal>
  </div>
</template>

<style scoped>
/* A rule to the left of the name marks the two members of an HA pair. It is
   positioned against the cell rather than sitting in the text flow, so it
   spans the full row and the two rows of a pair join into one line. Tones are
   deliberately neutral blue-greys: red, amber and green already mean device
   state in this table. */
.ha-mark {
  width: 2px;
  border-radius: 1px;
}

.ha-mark--a {
  background-color: #7a93b0;
}

.ha-mark--b {
  background-color: #c2ccd8;
}

.ha-mark--none {
  background-color: transparent;
}

/* Broken rather than solid: this member belongs to a pair whose other half is
   not on the table, so there is nothing for the line to join up with. */
.ha-mark--alone {
  background-color: transparent;
  background-image: repeating-linear-gradient(
    to bottom,
    #aab3c0 0,
    #aab3c0 3px,
    transparent 3px,
    transparent 6px
  );
}

/* The columns are fixed so they line up across rows; on a narrow window the
   container scrolls instead of letting the cells overlap. */
.sdwan-fixed-row {
  min-width: 680px;
}
/* On a small laptop - or any window under ~1100px - the fixed columns stop
   being an alignment aid and start forcing a horizontal scroll, so the row
   reflows into stacked lines instead. Inline widths need !important here. */
@media (max-width: 1100px) {
  .sdwan-fixed-row {
    min-width: 0;
    flex-wrap: wrap;
  }
  .sdwan-fixed-row > div {
    width: auto !important;
    flex: 0 0 auto !important;
    max-width: 100%;
    text-align: left !important;
  }
}
.help-tip {
  cursor: help;
  color: #ced4da;
}
.help-tip:hover {
  color: #98a6ad;
}
</style>

<style>
/* Unscoped on purpose: the svg inside vue-feather's root element does not
   carry the scoped attribute (scoped rules cannot reach it). */
/* Inline-flex wrapper + block svg keeps inline icons vertically centered on
   the text with no negative margins, so nothing can overflow or clip. */
.firewalls-page .icon-xs {
  display: inline-flex;
  align-items: center;
  vertical-align: -0.15em;
  line-height: 1;
}
.firewalls-page .icon-xs svg {
  display: block;
  margin: 0;
}
/* Help marks are annotations, not content: smaller and lighter than the
   decorative icons. Overrides the theme's .icon-xs > svg 16px !important. */
.firewalls-page .help-tip svg {
  width: 12px !important;
  height: 12px !important;
}
/* Unscoped because b-table renders these cells itself, so they never carry
   this component's scope attribute. Namespaced under .firewalls-page so the
   rules cannot reach any other page. */
.firewalls-page td.ha-cell,
.firewalls-page th.ha-cell-head {
  position: relative;
  padding-left: 20px;
}
.firewalls-page td.ha-cell .ha-mark {
  position: absolute;
  left: 8px;
  /* Overshooting by the width of the row border is what removes the seam
     between the two halves, so a pair reads as one unbroken line. */
  top: -1px;
  bottom: -1px;
}
/* The outer ends stop short of the row edge, which closes each pair off and
   keeps two pairs on consecutive rows from running into one another. */
.firewalls-page td.ha-cell .ha-mark--top {
  top: 7px;
}
.firewalls-page td.ha-cell .ha-mark--bottom {
  bottom: 7px;
}
/* A lone member has no neighbour to meet, so it is closed at both ends. */
.firewalls-page td.ha-cell .ha-mark--alone {
  top: 7px;
  bottom: 7px;
}
</style>
